import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AlertsPanel = ({ alerts, className = '' }) => {
  const [activeTab, setActiveTab] = useState('alerts');

  const getAlertIcon = (type) => {
    switch (type) {
      case 'critical': return 'AlertTriangle';
      case 'warning': return 'AlertCircle';
      case 'info': return 'Info';
      default: return 'Bell';
    }
  };

  const getAlertColor = (type) => {
    switch (type) {
      case 'critical': return 'text-error';
      case 'warning': return 'text-warning';
      case 'info': return 'text-primary';
      default: return 'text-muted-foreground';
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp)?.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const tabs = [
    { id: 'alerts', label: 'Alerts', count: alerts?.filter(a => a?.category === 'alert')?.length },
    { id: 'approvals', label: 'Approvals', count: alerts?.filter(a => a?.category === 'approval')?.length },
    { id: 'notifications', label: 'Updates', count: alerts?.filter(a => a?.category === 'notification')?.length }
  ];

  const filteredAlerts = alerts?.filter(alert => 
    activeTab === 'alerts' ? alert?.category === 'alert' :
    activeTab === 'approvals' ? alert?.category === 'approval' :
    alert?.category === 'notification'
  );

  return (
    <div className={`bg-card border border-border rounded-lg card-shadow ${className}`}>
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Executive Alerts</h3>
          <Button variant="ghost" size="sm" iconName="Settings">
            Configure
          </Button>
        </div>
        
        <div className="flex space-x-1">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-smooth ${
                activeTab === tab?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {tab?.label}
              {tab?.count > 0 && (
                <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                  activeTab === tab?.id
                    ? 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {tab?.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
      <div className="max-h-96 overflow-y-auto">
        {filteredAlerts?.length === 0 ? (
          <div className="p-6 text-center">
            <Icon name="CheckCircle" size={32} className="text-success mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No {activeTab} at this time</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredAlerts?.map((alert) => (
              <div key={alert?.id} className="p-4 hover:bg-muted/50 transition-smooth">
                <div className="flex items-start space-x-3">
                  <div className={`mt-0.5 ${getAlertColor(alert?.type)}`}>
                    <Icon name={getAlertIcon(alert?.type)} size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-foreground truncate">
                        {alert?.title}
                      </p>
                      <span className="text-xs text-muted-foreground ml-2">
                        {formatTime(alert?.timestamp)}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                      {alert?.message}
                    </p>
                    {alert?.category === 'approval' && (
                      <div className="flex items-center space-x-2 mt-3">
                        <Button variant="default" size="xs">
                          Approve
                        </Button>
                        <Button variant="outline" size="xs">
                          Review
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {filteredAlerts?.length > 0 && (
        <div className="p-4 border-t border-border">
          <Button variant="ghost" size="sm" fullWidth>
            View All {activeTab}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AlertsPanel;